import type { ExtensionTopic } from './types';

export enum MARKETPLACE {
    /**
     * @value Activated
     * @description Use to display if a marketplace extension is activated
     */
    ACTIVATED = 'MARKETPLACE.ACTIVATED',
    /**
     * @value Activation status
     * @description Use to display the activation status label on the extension list
     */
    ACTIVATION_STATUS = 'MARKETPLACE.ACTIVATION_STATUS',
    /**
     * @value Select sites where the extension will be executed
     */
    ALLOWED_SITES_HELPER = 'MARKETPLACE.ALLOWED_SITES_HELPER',
    /**
     * @value Select sites where this extension should run
     */
    ALLOWED_SITES_PLACEHOLDER = 'MARKETPLACE.ALLOWED_SITES_PLACEHOLDER',
    /**
     * @value All extensions are up to date !
     * @description Message to announce that all extensions are up to date
     */
    ALL_EXTENSIONS_UP_TO_DATE = 'MARKETPLACE.ALL_EXTENSIONS_UP_TO_DATE',
    /**
     * @value Available updates
     * @description Display available updates title for extensions
     */
    AVAILABLE_UPDATES = 'MARKETPLACE.AVAILABLE_UPDATES',
    /**
     * @value Choose the default connector to use for this extension
     */
    CHOOSE_DEFAULT_EXTENSION_CONNECTORS = 'MARKETPLACE.CHOOSE_DEFAULT_EXTENSION_CONNECTORS',
    /**
     * @value Choose the connector to use for this extension
     */
    CHOOSE_EXTENSION_CONNECTORS = 'MARKETPLACE.CHOOSE_EXTENSION_CONNECTORS',
    /**
     * @value Are you sure you want to uninstall the selected extensions?
     * @description Message displayed when uninstalling multiple marketplace extensions
     */
    CONFIRM_UNINSTALL_EXTENSIONS = 'MARKETPLACE.CONFIRM_UNINSTALL_EXTENSIONS',
    /**
     * @todo: [MA-709] Clean unused lokalise keys in repositories
     * @value Connector
     */
    CONNECTOR = 'MARKETPLACE.CONNECTOR',
    /**
     * @value Error while loading connector list
     */
    CONNECTOR_LIST_ERROR = 'MARKETPLACE.CONNECTOR_LIST_ERROR',
    /**
     * @value {"one":"1 day ago","other":"%NB% days ago"}
     * @description Number of days since extension update
     */
    DAY_SINCE_UPDATE = 'MARKETPLACE.DAY_SINCE_UPDATE',
    /**
     * @value Deactivated
     * @description Use to display if a marketplace extension is deactivated
     */
    DEACTIVATED = 'MARKETPLACE.DEACTIVATED',
    /**
     * @value Developer Portal
     * @description New side menu entry in the back office to ease the dev portal access
     */
    DEVELOPER_PORTAL = 'MARKETPLACE.DEVELOPER_PORTAL',
    /**
     * @value Extension disabled
     */
    DISABLED = 'MARKETPLACE.EXTENSION_DISABLED',
    /**
     * @value Embed code
     */
    EMBED_CODE = 'PLAY.EMBED_CODE',
    /**
     * @value Embed code copied to the clipboard
     */
    EMBED_CODE_COPIED = 'PLAY.EMBED_CODE_COPIED',
    /**
     * @value Extensions Library
     */
    EXTENSIONS_LIBRARY = 'ADMIN.NAV.EXTENSIONS_LIBRARY',
    /**
     * @value See more extensions
     */
    EXTENSIONS_SEE_MORE = 'ADMIN.EXTENSIONS.SEE_MORE',
    /**
     * @value Accounting & Finance
     * @description Accounting and finance topic title
     */
    EXTENSIONS_TOPIC_ACCOUNTING_AND_FINANCE = 'MARKETPLACE.EXTENSIONS_TOPIC_ACCOUNTING_AND_FINANCE',
    /**
     * @value Administration & Management
     * @description Administration & Management topic title
     */
    EXTENSIONS_TOPIC_ADMINISTRATION_AND_MANAGEMENT = 'MARKETPLACE.EXTENSIONS_TOPIC_ADMINISTRATION_AND_MANAGEMENT',
    /**
     * @value Analytics
     * @description Analytics topic title
     */
    EXTENSIONS_TOPIC_ANALYTICS = 'MARKETPLACE.EXTENSIONS_TOPIC_ANALYTICS',
    /**
     * @value Chat & Communication
     * @description Chat & Communication topic title
     */
    EXTENSIONS_TOPIC_CHAT_AND_COMMUNICATION = 'MARKETPLACE.EXTENSIONS_TOPIC_CHAT_AND_COMMUNICATION',
    /**
     * @value Collaboration
     * @description Collaboration topic title
     */
    EXTENSIONS_TOPIC_COLLABORATION = 'MARKETPLACE.EXTENSIONS_TOPIC_COLLABORATION',
    /**
     * @value Document & File management
     * @description Document & File management topic title
     */
    EXTENSIONS_TOPIC_DOCUMENT_AND_FILE_MANAGEMENT = 'MARKETPLACE.EXTENSIONS_TOPIC_DOCUMENT_AND_FILE_MANAGEMENT',
    /**
     * @value Education & Learnings
     * @description Education & Learnings topic title
     */
    EXTENSIONS_TOPIC_EDUCATION_AND_LEARNINGS = 'MARKETPLACE.EXTENSIONS_TOPIC_EDUCATION_AND_LEARNINGS',
    /**
     * @value ERP & Logistics
     * @description ERP & Logistics topic title
     */
    EXTENSIONS_TOPIC_ERP_AND_LOGISTICS = 'MARKETPLACE.EXTENSIONS_TOPIC_ERP_AND_LOGISTICS',
    /**
     * @value Gamification
     * @description Gamification topic title
     */
    EXTENSIONS_TOPIC_GAMIFICATION = 'MARKETPLACE.EXTENSIONS_TOPIC_GAMIFICATION',
    /**
     * @value Geolocation & Map services
     * @description Geolocation & Map services topic title
     */
    EXTENSIONS_TOPIC_GEOLOCATION_AND_MAP_SERVICES = 'MARKETPLACE.EXTENSIONS_TOPIC_GEOLOCATION_AND_MAP_SERVICES',
    /**
     * @value HR & Legal
     * @description HR & Legal topic title
     */
    EXTENSIONS_TOPIC_HR_AND_LEGAL = 'MARKETPLACE.EXTENSIONS_TOPIC_HR_AND_LEGAL',
    /**
     * @value IAM services
     * @description IAM services topic title
     */
    EXTENSIONS_TOPIC_IAM_SERVICES = 'MARKETPLACE.EXTENSIONS_TOPIC_IAM_SERVICES',
    /**
     * @value IT & Help Desk
     * @description IT & Help Desk topic title
     */
    EXTENSIONS_TOPIC_IT_AND_HELP_DESK = 'MARKETPLACE.EXTENSIONS_TOPIC_IT_AND_HELP_DESK',
    /**
     * @value Knowledge
     * @description Knowledge topic title
     */
    EXTENSIONS_TOPIC_KNOWLEDGE = 'MARKETPLACE.EXTENSIONS_TOPIC_KNOWLEDGE',
    /**
     * @value Marketing
     * @description Marketing topic title
     */
    EXTENSIONS_TOPIC_MARKETING = 'MARKETPLACE.EXTENSIONS_TOPIC_MARKETING',
    /**
     * @value News & Weather
     * @description News & Weather topic title
     */
    EXTENSIONS_TOPIC_NEWS_AND_WEATHER = 'MARKETPLACE.EXTENSIONS_TOPIC_NEWS_AND_WEATHER',
    /**
     * @value Operations
     * @description Operations topic title
     */
    EXTENSIONS_TOPIC_OPERATIONS = 'MARKETPLACE.EXTENSIONS_TOPIC_OPERATIONS',
    /**
     * @value Sales & CRM
     * @description Sales & CRM topic title
     */
    EXTENSIONS_TOPIC_SALES_AND_CRM = 'MARKETPLACE.EXTENSIONS_TOPIC_SALES_AND_CRM',
    /**
     * @value Search Services
     * @description Search Services topic title
     */
    EXTENSIONS_TOPIC_SEARCH_SERVICES = 'MARKETPLACE.EXTENSIONS_TOPIC_SEARCH_SERVICES',
    /**
     * @value Task management
     * @description Task management topic title
     */
    EXTENSIONS_TOPIC_TASK_MANAGEMENT = 'MARKETPLACE.EXTENSIONS_TOPIC_TASK_MANAGEMENT',
    /**
     * @value Travel & Expenses
     * @description Travel & Expenses topic title
     */
    EXTENSIONS_TOPIC_TRAVEL_AND_EXPENSES = 'MARKETPLACE.EXTENSIONS_TOPIC_TRAVEL_AND_EXPENSES',
    /**
     * @value Utilities
     * @description Utilities topic title
     */
    EXTENSIONS_TOPIC_UTILITIES = 'MARKETPLACE.EXTENSIONS_TOPIC_UTILITIES',
    /**
     * @value Extension activated!
     */
    EXTENSION_ACTIVATED = 'ADMIN.MARKETPLACE.EXTENSION.ACTIVATED',
    /**
     * @value Available connectors
     */
    EXTENSION_CONNECTORS = 'ADMIN.MARKETPLACE.EXTENSION.SETTINGS.CONNECTORS_TITLE',
    /**
     * @value Extension could not load.
     */
    EXTENSION_COULD_NOT_LOAD = 'FRONT.MARKETPLACE.EXTENSION_COULD_NOT_LOAD',
    /**
     * @value Extension deactivated!
     */
    EXTENSION_DEACTIVATED = 'ADMIN.MARKETPLACE.EXTENSION.DEACTIVATED',
    /**
     * @value Loading
     */
    EXTENSION_LOADIND_INSTALL = 'ADMIN.MARKETPLACE.EXTENSION.LOADING_INSTALL',
    /**
     * @value Allow to save micro-app logs for debugging.
     * @description Helper for enable debug mode switch button
     */
    EXTENSION_RUNS_WILL_BE_LOGGED = 'MARKETPLACE.EXTENSION_RUNS_WILL_BE_LOGGED',
    /**
     * @value Save run history
     * @description Toggle button to enable debug mode
     */
    EXTENSION_SAVE_RUNS = 'MARKETPLACE.EXTENSION_SAVE_RUNS',
    /**
     * @value Extension settings
     */
    EXTENSION_SETTINGS = 'ADMIN.MARKETPLACE.EXTENSION.SETTINGS_TITLE',
    /**
     * @value Extension settings saved
     * @description Success message display when extension settings are saved
     */
    EXTENSION_SETTINGS_SAVED = 'MARKETPLACE.EXTENSION_SETTINGS_SAVED',
    /**
     * @value Connectors
     */
    EXTENSION_TAB_CONNECTOR = 'ADMIN.MARKETPLACE.EXTENSION.SETTINGS.CONNECTORS_TAB',
    /**
     * @value Run history
     * @description Run history tab from marketplace extension details
     */
    EXTENSION_TAB_RUN_HISTORY = 'ADMIN.MARKETPLACE.EXTENSION.SETTINGS.RUN_HISTORY_TAB',
    /**
     * Use to call dynamically all the extension type key below
     */
    EXTENSION_TYPE = 'MARKETPLACE.EXTENSION_TYPE',
    /**
     * @value Backend
     * @description Display backend type for marketplace extension
     */
    EXTENSION_TYPE_BACKEND = 'MARKETPLACE.EXTENSION_TYPE_BACKEND',
    /**
     * @value Broadcast
     * @description Type broadcast for marketplace extensions
     */
    EXTENSION_TYPE_BROADCAST = 'MARKETPLACE.EXTENSION_TYPE_BROADCAST',
    /**
     * @value Login
     */
    EXTENSION_TYPE_LOGIN = 'MARKETPLACE.EXTENSION_TYPE_LOGIN',
    /**
     * @value Micro-app
     * @description Type microapp for marketplace extensions
     */
    EXTENSION_TYPE_MICRO_APP = 'MARKETPLACE.EXTENSION_TYPE_MICRO_APP',
    /**
     * @value Provisioning
     */
    EXTENSION_TYPE_PROVISIONING = 'MARKETPLACE.EXTENSION_TYPE_PROVISIONING',
    /**
     * @value Share to
     * @description Display share to type for marketplace extension
     */
    EXTENSION_TYPE_SHARE_TO = 'MARKETPLACE.EXTENSION_TYPE_SHARE_TO',
    /**
     * @value Third party
     * @description Display third party type for marketplace extension
     */
    EXTENSION_TYPE_THIRD_PARTY = 'MARKETPLACE.EXTENSION_TYPE_THIRD_PARTY',
    /**
     * @value Widget
     * @description Display widget type for marketplace extension
     */
    EXTENSION_TYPE_WIDGET = 'MARKETPLACE.EXTENSION_TYPE_WIDGET',
    /**
     * @value Extension unavailable
     * @description Use to display a warning showing that the extension had been unavailable
     */
    EXTENSION_UNAVAILABLE = 'MARKETPLACE.EXTENSION_UNAVAILABLE',
    /**
     * @value Extension uninstalled
     */
    EXTENSION_UNINSTALLED = 'ADMIN.MARKETPLACE.EXTENSION.UNINSTALLED',
    /**
     * @value Are you sure you want to uninstall this extension?
     */
    EXTENSION_UNINSTALL_CONFIRM = 'ADMIN.MARKETPLACE.EXTENSION.UNINSTALL.CONFIRM',
    /**
     * @value Uninstall an extension
     */
    EXTENSION_UNINSTALL_CONFIRM_TITLE = 'ADMIN.MARKETPLACE.EXTENSION.UNINSTALL.CONFIRM_TITLE',
    /**
     * @value Extension updated!
     * @description Use to display the success message while updating an extension
     */
    EXTENSION_UPDATED = 'MARKETPLACE.EXTENSION_UPDATED',
    /**
     * @value Extension will be available on your platform
     * @description Display information message about the activation of an extension
     */
    EXTENSION_WILL_BE_AVAILABLE = 'MARKETPLACE.EXTENSION_WILL_BE_AVAILABLE',
    /**
     * @value Extension global settings
     */
    GLOBAL_SETTINGS = 'ADMIN.EXTENSIONS.GLOBAL_SETTINGS',
    /**
     * @value Add groups to restrict visibility.
     * @description Helper message that indicates the user that he can add groups thanks to the input to restrict visibility of the current feature
     */
    GROUP_VISIBILITY_HELPER = 'MARKETPLACE.GROUP_VISIBILITY_HELPER',
    /**
     * @value This extension uses the LumApps customization API which can change your platform design
     */
    HAS_CUSTOMIZATION_API_SCRIPT = 'MARKETPLACE.EXTENSION_HAS_CUSTOMIZATION_SCRIPT',
    /**
     * @value Installation
     * @description Use to display the installation date label on the extension list
     */
    INSTALLATION = 'MARKETPLACE.INSTALLATION',
    /**
     * @value Installation date
     * @description Extension installation date title
     */
    INSTALLATION_DATE = 'MARKETPLACE.INSTALLATION_DATE',
    /**
     * @value Extension "%EXTENSION_NAME%" could not be installed.
     */
    INSTALLATION_ERROR = 'ADMIN.MARKETPLACE.INSTALLATION_ERROR',
    /**
     * @value Extension "%EXTENSION_NAME%" successfully installed.
     * @description Extension "%EXTENSION_NAME%" successfully installed.
     */
    INSTALLATION_SUCCESS = 'ADMIN.MARKETPLACE.INSTALLATION_SUCCESS',
    /**
     * @value Installed after
     * @description Label for extensions installed after a chosen date
     */
    INSTALLED_AFTER = 'MARKETPLACE.INSTALLED_AFTER',
    /**
     * @value Installed before
     * @description Label for extensions installed before a chosen date
     */
    INSTALLED_BEFORE = 'MARKETPLACE.INSTALLED_BEFORE',
    /**
     * @value Installed by
     * @description Label to display by who an extension has been installed
     */
    INSTALLED_BY = 'MARKETPLACE.INSTALLED_BY',
    /**
     * @value Installed extensions
     */
    INSTALLED_EXTENSIONS = 'ADMIN.NAV.INSTALLED_EXTENSIONS',
    /**
     * @value Installed on
     * @description Label to display when an extension has been installed
     */
    INSTALLED_ON = 'MARKETPLACE.INSTALLED_ON',
    /**
     * @value Install updates
     * @description Label for the button to install updates for an extension
     */
    INSTALL_UPDATES = 'MARKETPLACE.INSTALL_UPDATES',
    /**
     * @value Run only on selected sites
     */
    IS_ALLOWED_BY_SITES_LABEL = 'MARKETPLACE.IS_ALLOWED_BY_SITES_LABEL',
    /**
     * @value New update available for %NAME%
     * @description Message to announce that a new update is available for a specific extension
     */
    NEW_UPDATE_AVAILABLE = 'MARKETPLACE.NEW_UPDATE_AVAILABLE',
    /**
     * @value Oauth application
     */
    OAUTH_APP = 'ADMIN.MARKETPLACE.APPLICATIONS.OAUTH_APP',
    /**
     * @value Partner
     * @description Display partner label for an extension
     */
    PARTNER = 'MARKETPLACE.PARTNER',
    /**
     * @value The partner has release a new update
     * @description Display information about new update for an extension
     */
    PARTNER_NEW_UPDATE = 'MARKETPLACE.PARTNER_NEW_UPDATE',
    /**
     * @value Extensions
     */
    PLATFORM_EXTENSIONS = 'ADMIN.NAV.PLATFORM_EXTENSIONS',
    PROVIDER_ERROR = 'MARKETPLACE.PROVIDER_LIST_ERROR',
    /**
     * @value Remove extension from your platform
     * @description Display information message about uninstalling an extension
     */
    REMOVE_EXTENSION_FROM_PLATFORM = 'MARKETPLACE.REMOVE_EXTENSION_FROM_PLATFORM',
    /**
     * @value See more applications
     */
    SEE_MORE = 'ADMIN.MARKETPLACE.APPLICATIONS.SEE_MORE',
    /**
     * @value Select a connector
     * @description Placeholder for connector selector in extension settings and extension global settings
     */
    SELECT_CONNECTOR = 'MARKETPLACE.SELECT_CONNECTOR',
    /**
     * @value Select Oauth application
     */
    SELECT_OAUTH = 'ADMIN.MARKETPLACE.APPLICATIONS.SELECT_OAUTH',
    /**
     * @value Site execution and visibility
     */
    SITE_VISIBILITY = 'MARKETPLACE.SITE_VISIBILITY',
    /**
     * @value By default, this extension runs on all sites.
     */
    SITE_VISIBILITY_INFO = 'MARKETPLACE.SITE_VISIBILITY_INFO',
    /**
     * @value Unavailable
     * @description Use to display if a marketplace extension is unavailable
     */
    UNAVAILABLE = 'MARKETPLACE.UNAVAILABLE',
    /**
     * @value Extension unavailable. Replace widget.
     */
    UNAVAILABLE_REPLACE = 'MARKETPLACE.UNAVAILABLE_REPLACE',
    /**
     * @value Uninstall
     */
    UNINSTALL = 'ADMIN.MARKETPLACE.EXTENSION.UNINSTALL',
    /**
     * @value Uninstall extension
     * @description Display uninstall extension title
     */
    UNINSTALL_EXTENSION = 'MARKETPLACE.UNINSTALL_EXTENSION',
    /**
     * @value Uninstall extensions
     * @description Display uninstall extensions title
     */
    UNINSTALL_EXTENSIONS = 'MARKETPLACE.UNINSTALL_EXTENSIONS',
    /**
     * @value Upgraded on
     * @description Label to display when an extension has been upgraded
     */
    UPGRADED_ON = 'MARKETPLACE.UPGRADED_ON',
    /**
     * @value Version
     * @description Display version title for extensions
     */
    VERSION = 'MARKETPLACE.VERSION',
    /**
     * @value Compatible with mobile application
     * @description Display information if the extension is compatible with the mobile application
     */
    VERSION_MOBILE_COMPATIBLE = 'MARKETPLACE.VERSION_MOBILE_COMPATIBLE',
    /**
     * @value Incompatible with mobile application
     * @description Display information if the extension is incompatible with the mobile application
     */
    VERSION_MOBILE_INCOMPATIBLE = 'MARKETPLACE.VERSION_MOBILE_INCOMPATIBLE',
}
export const getExtensionsTopicKey = (topic: ExtensionTopic) => `MARKETPLACE.EXTENSIONS_TOPIC_${topic.toUpperCase()}`;
